import { useState, useEffect } from "react"
import { Link, useParams } from "react-router"
import { DashboardShell } from "@/components/dashboard-shell"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Separator } from "@/components/ui/separator"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  MapPin,
  Zap,
  Building,
  FileText,
  CheckCircle2,
  Clock,
  Download,
  Share2,
  Loader2,
  ExternalLink,
  Calendar,
  Globe,
  User,
  Radio,
  Gauge,
} from "lucide-react"
import { api, substationApi, type ProjectGridConnection } from "@/lib/api"
import { SubstationMap } from "@/components/SubstationMap"

interface DeveloperInfo {
  id: string
  name: string
  normalizedName: string
  companyNumber: string | null
  country: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
}

interface ProjectDeveloper {
  id: string
  created_at: string
  updated_at: string
  role: string
  isActive: boolean
  source: string
  isPartnership: boolean
  developer: DeveloperInfo
}

interface Capacity {
  id: string
  created_at: string
  updated_at: string
  capacityMw: number
  capacityType: string
  effectiveDate: string | null
  isCurrent: boolean
  source: string
}

interface StatusHistory {
  id: string
  created_at: string
  updated_at: string
  status: string
  subStatus: string | null
  statusDate: string
  isCurrent: boolean
  source: string
}

interface ProjectDetail {
  id: string
  created_at: string
  updated_at: string
  name: string
  normalizedName: string
  technology: string | null
  subTechnology: string | null
  lifecycleStage: string | null
  isActive: boolean
  latitude: number | null
  longitude: number | null
  region: string | null
  country: string | null
  postcode: string | null
  planningAuthority: string | null
  county: string | null
  planningApplicationReference: string | null
  sourceUpdatedAt: string | null
  sourceName: string | null
  lastUpdatedAt: string | null
  capacities: Capacity[]
  statusHistory: StatusHistory[]
  developers: ProjectDeveloper[]
}

interface CompanyDetail {
  id: string
  name: string
  company_number: string
  company_status: string
  company_type: string
  date_of_creation: string
  date_of_cessation: string | null
  office_address: string
  locality: string
  postal_code: string
  region: string
  country: string | null
}

interface CompanyOfficer {
  id: string
  name: string
  company_number: string
  appointed_on: string
  address: string
  country: string
  locality: string
  postal_code: string
  premises: string
  region: string
  nationality: string
  occupation: string
  officer_role: string
  person_of_most_significant: boolean
}

interface PlanningData {
  id: string
  projectId: string
  planningApplicationReference: string
  authorityName: string
  siteAddress: string
  applicationDecisionStatus: string
  decisionDescription: string | null
  decisionIssuedDate: string | null
  decisionTargetDate: string | null
  applicationReceivedDate: string | null
  applicationTypeCategory: string
  proposalDescription: string
  siteFullAddress: string | null
  documentsUrl: string | null
  lpaApplicationUrl: string | null
  parishName: string | null
  wardName: string | null
  permissionExpiryDate: string | null
  latestAuthorityActivity: string | null
  easting: number | null
  northing: number | null
  provider: string
  lastSyncedAt: string | null
  rawPayload: {
    applicant_organisation_name: string | null
    decision_type_description: string | null
    standard_consultation_start_date: string | null
    statutory_consultation_expiry_date: string | null
    appeal_lodged_date: string | null
    appeal_decision_date: string | null
    appeal_status_description: string | null
    appeal_decision_description: string | null
    [key: string]: unknown
  } | null
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "—"
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

function constraintStatusClasses(status: string | null): string {
  switch (status?.toUpperCase()) {
    case "GREEN":
      return "bg-green-500/10 border-green-500/20 text-green-500"
    case "AMBER":
      return "bg-amber-500/10 border-amber-500/20 text-amber-500"
    case "RED":
      return "bg-red-500/10 border-red-500/20 text-red-500"
    default:
      return ""
  }
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (!value || value === "—") return null
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  )
}

export default function ProjectDetailPage() {
  const { id } = useParams()
  const [project, setProject] = useState<ProjectDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [planningData, setPlanningData] = useState<PlanningData | null>(null)
  const [planningLoading, setPlanningLoading] = useState(false)

  const [companyDetails, setCompanyDetails] = useState<Record<string, CompanyDetail>>({})
  const [companyOfficers, setCompanyOfficers] = useState<Record<string, CompanyOfficer[]>>({})
  const [downloadLoading, setDownloadLoading] = useState(false)

  const [substationConnections, setSubstationConnections] = useState<ProjectGridConnection[]>([])
  const [substationLoading, setSubstationLoading] = useState(false)
  const [substationError, setSubstationError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    setLoading(true)
    setError(null)
    api
      .get<{ data: ProjectDetail }>(`/projects/${id}`)
      .then((res) => setProject(res.data))
      .catch((err) => setError(err?.message ?? "Failed to load project"))
      .finally(() => setLoading(false))
  }, [id])

  useEffect(() => {
    if (!id) return
    setPlanningLoading(true)
    api
      .get<{ data: PlanningData }>(`/remote/planning-data/${id}`)
      .then((res) => setPlanningData(res.data))
      .catch(() => setPlanningData(null))
      .finally(() => setPlanningLoading(false))
  }, [id])

  useEffect(() => {
    if (!id) return
    setSubstationLoading(true)
    setSubstationError(null)
    substationApi
      .syncProjectConnections(id)
      .then((res) => setSubstationConnections(res.data))
      .catch((err) => setSubstationError(err?.message ?? "Failed to load substation data"))
      .finally(() => setSubstationLoading(false))
  }, [id])

  useEffect(() => {
    if (!project?.developers.length) return
    project.developers.forEach((pd) => {
      const name = pd.developer.name
      if (companyDetails[name]) return
      api
        .get<{ data: CompanyDetail }>(`/remote/company?search=${encodeURIComponent(name)}`)
        .then((res) => {
          setCompanyDetails((prev) => ({ ...prev, [name]: res.data }))
        })
        .catch(() => {})
    })
  }, [project?.developers])

  useEffect(() => {
    Object.values(companyDetails).forEach((company) => {
      if (companyOfficers[company.company_number] !== undefined) return
      api
        .get<{ data: CompanyOfficer[] }>(`/remote/company-officers/${company.company_number}`)
        .then((res) => {
          setCompanyOfficers((prev) => ({ ...prev, [company.company_number]: res.data }))
        })
        .catch(() => {
          setCompanyOfficers((prev) => ({ ...prev, [company.company_number]: [] }))
        })
    })
  }, [companyDetails])

  const handleDownload = async () => {
    if (!project) return
    setDownloadLoading(true)
    try {
      const [{ pdf }, { default: ProjectPDF }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('@/components/ProjectPDF'),
      ])
      const logoUrl = `${window.location.origin}/kanu-logo.png`
      const blob = await pdf(
        <ProjectPDF
          project={project}
          planningData={planningData}
          companyDetails={companyDetails}
          companyOfficers={companyOfficers}
          logoUrl={logoUrl}
          substationConnections={substationConnections}
        />
      ).toBlob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${project.name.replace(/[^a-z0-9]+/gi, '_').toLowerCase()}-report.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } finally {
      setDownloadLoading(false)
    }
  }

  const header = (title: string) => (
    <header className="flex h-16 shrink-0 items-center gap-2 border-b border-border/50 bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60">
      <div className="flex items-center gap-2 px-4">
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mr-2 h-4" />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/dashboard/projects">Projects</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{title}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>
    </header>
  )

  if (loading) {
    return (
      <DashboardShell>
        {header("Loading...")}
        <div className="flex flex-1 items-center justify-center p-6">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardShell>
    )
  }

  if (error || !project) {
    return (
      <DashboardShell>
        {header("Project Not Found")}
        <div className="flex flex-1 items-center justify-center p-6">
          <Card className="max-w-md text-center">
            <CardHeader>
              <CardTitle>Project Not Found</CardTitle>
              <CardDescription>{error ?? "The project you're looking for doesn't exist."}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild>
                <Link to="/dashboard/projects">Back to Projects</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </DashboardShell>
    )
  }

  const currentCapacity = project.capacities.find((c) => c.isCurrent) ?? project.capacities[0]
  const currentStatus = project.statusHistory.find((s) => s.isCurrent) ?? project.statusHistory[0]
  const locationParts = [project.county, project.region, project.country].filter(Boolean)

  return (
    <DashboardShell>
      {header(project.name)}

      <div className="flex flex-1 flex-col gap-6 p-6">
        {/* Project Header */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              {currentStatus && (
                <Badge variant="default">{capitalize(currentStatus.status)}</Badge>
              )}
              {project.lifecycleStage && (
                <Badge variant="outline">{capitalize(project.lifecycleStage)}</Badge>
              )}
              {!project.isActive && (
                <Badge variant="destructive">Inactive</Badge>
              )}
            </div>
            <h1 className="text-4xl font-bold tracking-tight mb-2">{project.name}</h1>
            <div className="flex flex-wrap gap-4 text-sm mt-4">
              {locationParts.length > 0 && (
                <div className="flex items-center gap-1.5">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>{locationParts.join(", ")}</span>
                </div>
              )}
              {project.technology && (
                <div className="flex items-center gap-1.5">
                  <Zap className="h-4 w-4 text-primary" />
                  <span>{project.technology}{project.subTechnology ? ` · ${project.subTechnology}` : ""}</span>
                </div>
              )}
              {currentCapacity && (
                <div className="flex items-center gap-1.5">
                  <Zap className="h-4 w-4 text-muted-foreground" />
                  <span>{currentCapacity.capacityMw} MW ({capitalize(currentCapacity.capacityType)})</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleDownload} disabled={downloadLoading}>
              {downloadLoading
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <>Download <Download className="h-4 w-4" /></>
              }
            </Button>
          </div>
        </div>

        {/* Quick stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Capacity</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {currentCapacity ? `${currentCapacity.capacityMw} MW` : "—"}
              </div>
              {currentCapacity && (
                <p className="text-xs text-muted-foreground mt-1">{capitalize(currentCapacity.capacityType)}</p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Planning Authority</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-semibold leading-tight">
                {project.planningAuthority ?? "—"}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Data Source</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-semibold">{project.sourceName ?? "—"}</div>
              {project.lastUpdatedAt && (
                <p className="text-xs text-muted-foreground mt-1">
                  Updated {formatDate(project.lastUpdatedAt)}
                </p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Developers</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{project.developers.length}</div>
              {project.developers.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1 truncate">
                  {project.developers.map((d) => d.developer.name).join(", ")}
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="developers">Developers</TabsTrigger>
            <TabsTrigger value="planning-data">Planning Data</TabsTrigger>
            <TabsTrigger value="substation-intelligence">Substation Intelligence</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6 space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary" />
                    Location
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[
                    ["Region", project.region],
                    ["Country", project.country],
                    ["County", project.county],
                    ["Postcode", project.postcode],
                  ].map(([label, value]) =>
                    value ? (
                      <div key={label} className="flex justify-between">
                        <span className="text-muted-foreground">{label}</span>
                        <span className="font-medium">{value}</span>
                      </div>
                    ) : null
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary" />
                    Planning
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Authority</span>
                    <span className="font-medium">{project.planningAuthority ?? "—"}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Reference</span>
                    <span className="font-medium font-mono text-sm">
                      {project.planningApplicationReference ?? "—"}
                    </span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Current Status</span>
                    {currentStatus ? (
                      <Badge variant="default">{capitalize(currentStatus.status)}</Badge>
                    ) : (
                      <span className="font-medium">—</span>
                    )}
                  </div>
                  {planningData && (
                    <>
                      <Separator />
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Decision</span>
                        <Badge variant="secondary">{planningData.applicationDecisionStatus ? capitalize(planningData.applicationDecisionStatus) : "—"}</Badge>
                      </div>
                      {planningData.applicationReceivedDate && (
                        <>
                          <Separator />
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Received</span>
                            <span className="font-medium">{formatDate(planningData.applicationReceivedDate)}</span>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building className="h-5 w-5 text-primary" />
                    Project Info
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-3 md:grid-cols-2">
                    {[
                      ["Technology", project.technology],
                      ["Sub-technology", project.subTechnology],
                      ["Lifecycle Stage", currentStatus ? capitalize(currentStatus.status) : null],
                      ["Source", project.sourceName],
                      ["Source Updated", formatDate(project.sourceUpdatedAt)],
                      ["Last Updated", formatDate(project.lastUpdatedAt)],
                    ].map(([label, value]) =>
                      value ? (
                        <div key={label} className="flex justify-between">
                          <span className="text-muted-foreground">{label}</span>
                          <span className="font-medium">{value}</span>
                        </div>
                      ) : null
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="developers" className="mt-6">
            {project.developers.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  No developers recorded for this project.
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-6 lg:grid-cols-2">
                {project.developers.map((pd) => {
                  const company = companyDetails[pd.developer.name]
                  const officers = company ? (companyOfficers[company.company_number] ?? null) : null
                  const directors = officers?.filter((o) => o.officer_role === "director") ?? []
                  const secretaries = officers?.filter((o) => o.officer_role === "secretary") ?? []
                  const otherOfficers = officers?.filter((o) => o.officer_role !== "director" && o.officer_role !== "secretary") ?? []

                  return (
                    <Card key={pd.id} className="flex flex-col">
                      {/* Header */}
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <User className="h-5 w-5 text-primary shrink-0" />
                            <CardTitle className="text-lg leading-tight truncate">{pd.developer.name}</CardTitle>
                          </div>
                          <div className="flex gap-2 shrink-0">
                            <Badge variant="outline">{capitalize(pd.role)}</Badge>
                            {pd.isPartnership && <Badge variant="secondary">Partnership</Badge>}
                          </div>
                        </div>
                      </CardHeader>

                      <CardContent className="flex flex-col gap-5 flex-1">
                        {/* Developer info */}
                        <div className="space-y-2 text-sm">
                          <DetailRow label="Status" value={pd.isActive ? "Active" : "Inactive"} />
                          <DetailRow label="Source" value={pd.source} />
                          {pd.developer.country && (
                            <DetailRow label="Country" value={pd.developer.country} />
                          )}
                          {pd.developer.companyNumber && (
                            <DetailRow
                              label="Company No."
                              value={<span className="font-mono">{pd.developer.companyNumber}</span>}
                            />
                          )}
                        </div>

                        {/* Registry detail */}
                        {company === undefined ? (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground py-1">
                            <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                            Loading registry data…
                          </div>
                        ) : company ? (
                          <>
                            <Separator />
                            <div className="space-y-2 text-sm">
                              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                Registry Detail
                              </p>
                              <p className="font-medium">{company.name}</p>
                              <DetailRow
                                label="Company No."
                                value={<span className="font-mono">{company.company_number}</span>}
                              />
                              <DetailRow label="Type" value={company.company_type.toUpperCase()} />
                              <DetailRow
                                label="Status"
                                value={
                                  <Badge
                                    variant={company.company_status === "active" ? "default" : "destructive"}
                                    className="text-xs"
                                  >
                                    {capitalize(company.company_status)}
                                  </Badge>
                                }
                              />
                              <DetailRow label="Incorporated" value={formatDate(company.date_of_creation)} />
                              {company.date_of_cessation && (
                                <DetailRow label="Dissolved" value={formatDate(company.date_of_cessation)} />
                              )}
                              {(company.office_address || company.locality) && (
                                <DetailRow
                                  label="Registered Address"
                                  value={[company.office_address, company.locality, company.postal_code]
                                    .filter(Boolean)
                                    .join(", ")}
                                />
                              )}
                              {company.region && <DetailRow label="Region" value={company.region} />}
                            </div>
                          </>
                        ) : null}

                        {/* Officers */}
                        {company && (
                          <>
                            <Separator />
                            <div className="space-y-4">
                              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                Company Officers
                              </p>
                              {officers === null ? (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                                  Loading officers…
                                </div>
                              ) : officers.length === 0 ? (
                                <p className="text-sm text-muted-foreground">No officers on record.</p>
                              ) : (
                                <div className="space-y-4">
                                  {[
                                    { label: "Directors", list: directors },
                                    { label: "Secretaries", list: secretaries },
                                    { label: "Other Officers", list: otherOfficers },
                                  ]
                                    .filter(({ list }) => list.length > 0)
                                    .map(({ label, list }) => (
                                      <div key={label}>
                                        <p className="text-xs text-muted-foreground mb-2">{label}</p>
                                        <div className="grid gap-2 sm:grid-cols-2">
                                          {list.map((officer) => (
                                            <div
                                              key={officer.id}
                                              className="flex flex-col gap-0.5 rounded-lg border p-3 text-sm"
                                            >
                                              <span className="font-medium leading-tight">{officer.name}</span>
                                              {officer.nationality && (
                                                <span className="text-xs text-muted-foreground">{officer.nationality}</span>
                                              )}
                                              <span className="text-xs text-muted-foreground mt-1">
                                                Appointed {formatDate(officer.appointed_on)}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="planning-data" className="mt-6">
            {planningLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : !planningData ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  No planning data available for this project.
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {/* Application Overview */}
                <Card className="">
                  <CardHeader>
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <FileText className="h-5 w-5 text-primary" />
                          Application Overview
                        </CardTitle>
                        {planningData.proposalDescription && (
                          <CardDescription className="mt-2 text-sm leading-relaxed">
                            {planningData.proposalDescription}
                          </CardDescription>
                        )}
                      </div>
                      <Badge
                        variant={planningData.applicationDecisionStatus?.toLowerCase() === "permitted" ? "default" : "secondary"}
                      >
                        {planningData.applicationDecisionStatus ? capitalize(planningData.applicationDecisionStatus) : "—"}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-3 md:grid-cols-1">
                      <DetailRow
                        label="Reference"
                        value={<span className="font-mono text-sm">{planningData.planningApplicationReference}</span>}
                      />
                      <DetailRow label="Authority" value={planningData.authorityName} />
                      <DetailRow label="Type" value={planningData.applicationTypeCategory ? capitalize(planningData.applicationTypeCategory) : undefined} />
                      <DetailRow
                        label="Provider"
                        value={planningData.provider}
                      />
                      {planningData.rawPayload?.applicant_organisation_name && (
                        <DetailRow
                          label="Applicant"
                          value={planningData.rawPayload.applicant_organisation_name}
                        />
                      )}
                      {planningData.rawPayload?.decision_type_description && (
                        <DetailRow
                          label="Decision Type"
                          value={planningData.rawPayload.decision_type_description}
                        />
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Site Information */}
                <Card className="col-start-1 row-start-2">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-primary" />
                      Site Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <DetailRow
                      label="Address"
                      value={planningData.siteFullAddress ?? planningData.siteAddress}
                    />
                    {planningData.parishName && (
                      <DetailRow label="Parish" value={planningData.parishName} />
                    )}
                    {planningData.wardName && (
                      <DetailRow label="Ward" value={planningData.wardName} />
                    )}
                    {planningData.easting != null && planningData.northing != null && (
                      <DetailRow
                        label="Coordinates"
                        value={
                          <span className="font-mono text-xs">
                            E {Math.round(planningData.easting)} / N {Math.round(planningData.northing)}
                          </span>
                        }
                      />
                    )}
                  </CardContent>
                </Card>

                {/* Key Dates */}
                <Card className="row-span-2 col-start-2 row-start-1">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-primary" />
                      Key Dates
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <DetailRow
                      label="Received"
                      value={formatDate(planningData.applicationReceivedDate)}
                    />
                    <DetailRow
                      label="Decision Target"
                      value={formatDate(planningData.decisionTargetDate)}
                    />
                    <DetailRow
                      label="Decision Issued"
                      value={formatDate(planningData.decisionIssuedDate)}
                    />
                    <DetailRow
                      label="Permission Expiry"
                      value={formatDate(planningData.permissionExpiryDate)}
                    />
                    {planningData.rawPayload?.standard_consultation_start_date && (
                      <DetailRow
                        label="Consultation Start"
                        value={formatDate(planningData.rawPayload.standard_consultation_start_date as string)}
                      />
                    )}
                    {planningData.rawPayload?.statutory_consultation_expiry_date && (
                      <DetailRow
                        label="Consultation Expiry"
                        value={formatDate(planningData.rawPayload.statutory_consultation_expiry_date as string)}
                      />
                    )}
                    <DetailRow
                      label="Last Authority Activity"
                      value={formatDate(planningData.latestAuthorityActivity)}
                    />
                    <DetailRow
                      label="Last Synced"
                      value={formatDate(planningData.lastSyncedAt)}
                    />
                  </CardContent>
                </Card>

                {/* Appeal Information (if any) */}
                {(planningData.rawPayload?.appeal_lodged_date ||
                  planningData.rawPayload?.appeal_status_description ||
                  planningData.rawPayload?.appeal_decision_description) && (
                  <Card className="col-span-2 row-start-3">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Globe className="h-5 w-5 text-primary" />
                        Appeal
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                      {planningData.rawPayload?.appeal_lodged_date && (
                        <DetailRow
                          label="Lodged"
                          value={formatDate(planningData.rawPayload.appeal_lodged_date as string)}
                        />
                      )}
                      {planningData.rawPayload?.appeal_decision_date && (
                        <DetailRow
                          label="Decision Date"
                          value={formatDate(planningData.rawPayload.appeal_decision_date as string)}
                        />
                      )}
                      {planningData.rawPayload?.appeal_status_description && (
                        <DetailRow
                          label="Status"
                          value={planningData.rawPayload.appeal_status_description as string}
                        />
                      )}
                      {planningData.rawPayload?.appeal_decision_description && (
                        <DetailRow
                          label="Decision"
                          value={planningData.rawPayload.appeal_decision_description as string}
                        />
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* External Links */}
                {planningData.lpaApplicationUrl && (
                  <Card className={planningData.rawPayload?.appeal_lodged_date ? "" : "md:col-span-2"}>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <ExternalLink className="h-5 w-5 text-primary" />
                        External Links
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <a
                        href={planningData.lpaApplicationUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-primary hover:underline"
                      >
                        <ExternalLink className="h-4 w-4 shrink-0" />
                        View on LPA Portal
                      </a>
                      {planningData.documentsUrl && (
                        <a
                          href={planningData.documentsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-primary hover:underline"
                        >
                          <FileText className="h-4 w-4 shrink-0" />
                          View Documents
                        </a>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="substation-intelligence" className="mt-6 space-y-6">
            {substationLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : substationError ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  {substationError}
                </CardContent>
              </Card>
            ) : substationConnections.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  {project.latitude == null || project.longitude == null
                    ? "This project has no location data, so nearby substations cannot be calculated."
                    : "No nearby substations found for this project."}
                </CardContent>
              </Card>
            ) : (
              <>
                {project.latitude != null && project.longitude != null && (
                  <SubstationMap
                    projectName={project.name}
                    projectLatitude={project.latitude}
                    projectLongitude={project.longitude}
                    connections={substationConnections}
                  />
                )}

                <div className="grid gap-4 md:grid-cols-2">
                  {substationConnections.map((connection) => {
                    const { substation } = connection
                    return (
                      <Card key={connection.id}>
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <Radio className="h-5 w-5 text-primary shrink-0" />
                              <div className="min-w-0">
                                <CardTitle className="text-lg leading-tight truncate">
                                  {substation.name}
                                </CardTitle>
                                <CardDescription>
                                  {[substation.type, substation.dno, substation.region]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </CardDescription>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2 shrink-0">
                              <Badge variant="outline">
                                {connection.rank === 1 ? "Nearest" : `#${connection.rank}`}
                              </Badge>
                              {substation.constraintStatus && (
                                <Badge
                                  variant="outline"
                                  className={constraintStatusClasses(substation.constraintStatus)}
                                >
                                  {capitalize(substation.constraintStatus)}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div className="rounded-lg border p-3">
                              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                <MapPin className="h-3.5 w-3.5" />
                                Distance
                              </div>
                              <div className="text-lg font-semibold mt-1">
                                {Number(connection.distanceKm).toFixed(2)} km
                              </div>
                            </div>
                            <div className="rounded-lg border p-3">
                              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                <Gauge className="h-3.5 w-3.5" />
                                Headroom
                              </div>
                              <div className="text-lg font-semibold mt-1">
                                {substation.headroomMw != null
                                  ? `${Number(substation.headroomMw).toFixed(2)} MW`
                                  : "—"}
                              </div>
                            </div>
                          </div>

                          <Separator />

                          <div className="space-y-2 text-sm">
                            <DetailRow
                              label="Firm Capacity"
                              value={
                                substation.firmCapacityMva != null
                                  ? `${Number(substation.firmCapacityMva).toFixed(2)} MVA`
                                  : undefined
                              }
                            />
                            <DetailRow
                              label="Voltage"
                              value={substation.voltageKv != null ? `${substation.voltageKv} kV` : undefined}
                            />
                            <DetailRow
                              label="Feasibility Score"
                              value={
                                connection.feasibilityScore != null
                                  ? Number(connection.feasibilityScore).toFixed(2)
                                  : undefined
                              }
                            />
                            <DetailRow label="GSP Parent" value={substation.gspParent} />
                            <DetailRow label="Data Date" value={formatDate(substation.dataDate)} />
                          </div>
                        </CardContent>
                      </Card>
                    )
                  })}
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardShell>
  )
}
